// Phase 1 — Authentication — redesign 2026-06-07 (v2 MUI).
//
// Five tabs: Policy / IdP / API keys / HMAC / Exempt paths. IdP, API key, and HMAC
// mint flows use slide-over drawers; secret reveal stays modal-driven.
//
// Backend wiring preserved verbatim:
//   asec.authPolicy / authPolicySet
//   asec.idpList / idpCreate / idpUpdate / idpRefresh / idpDisable / idpDelete
//   asec.apiKeyList / apiKeyCreate / apiKeyRevoke
//   asec.hmacKeyList / hmacKeyCreate / hmacKeyRevoke
//   asec.exemptList / exemptCreate / exemptDelete
//
// Hero "Requests · 24h" and "Auth failures" are derived from PG (these
// CH-backed counters do not have a per-site endpoint yet); placeholder
// values today, with a thin info banner on the Policy tab disclosing
// scope. The Policy mode/leeway/methods are fully live.
//
// Server-side enum is `log_only` for log mode; the UI shows "Log only".

import React from "react";
import {
  Stack, Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Paper, Select, MenuItem,
  Drawer,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Button,
  CircularProgress, InputLabel, TextField, Alert,
} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PauseIcon from "@mui/icons-material/Pause";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Key, Shield, ShieldCheck, ShieldX, Eye, Activity, Fingerprint, DoorOpen,
  SlidersHorizontal, Building2, Binary, GitMerge, Plus, Minus, Terminal,
  FileCode, CornerDownRight, TextCursorInput, Lock, Clock, List, PowerOff, X,
} from "lucide-react";

import asec from "../../../../../utils/axios/v1/asecAxios";
import { useNotification } from "../../../../../contexts/NotificationContext";
import colors from "../../../../../utils/colors";
import LayerdIcon from "../../../../common/LayerdIcon";
import FormHelpTooltip from "../../../../common/FormHelpTooltip";
import { PrimaryButton, GhostButton, SaveButton, CancelButton, formSaveButtonSx } from "../../../../common/Button";
import { GreenSwitch, GreenSelectForm, SafeTextField } from "../../../../common/Greens";
import { ReactComponent as ArrowdownIcon } from "../../../../../vendor/_new/icon/sites/arrow_down.svg";
import StickySaveBar from "../../../../common/StickySaveBar";
import { arraysEqual, collectObjectDiff, enabledLabel } from "../../../../common/stickySaveBarUtils";
import AsecPage from "./AsecPage";
import useAsecPermissions from "./useAsecPermissions";
import {
  asecTabsSx, asecSelectSx, asecTablePaperSx,
  asecSettingsTableHeadSx, asecSettingsTableBodySx,
  asecBtnPrimary, asecBtnOutlined, asecDialogActionsSx, asecFormPrimaryButtonSx, asecFormRowControlSx, asecFieldLabelSx, asecIconButtonSx,
  asecContentCardSx,
  AsecTextField, AsecField,
} from "./asecUi";
import {
  AsecMetricStrip, AsecModeSegmented, AsecStatusPill, AsecMono, AsecSectionCard, AsecHeroModeValue, AsecHeroStatValue,
} from "./components/asecPrimitives";
import { AsecEventsFilterBar } from "./components/AsecEventsFilterBar";
import { validateDescription, validateName, getCharPolicyFieldText } from "../../../../../utils/validation";

// MCP_MTLS_SITE_2026_11_03 — mtls_* are sent only when changed (see applyPolicy).
const POLICY_KEYS = ["mode", "require_auth", "accepted_methods", "required_methods", "leeway_seconds"];
const MTLS_KEYS = ["mtls_ca_pem", "mtls_cert_pins", "mtls_allowed_subjects"];

const AUTH_POLICY_DIFF_FIELDS = [
  { key: "mode", label: "Active mode", format: (v) => MODES.find((m) => m.value === v)?.label ?? String(v ?? "—") },
  { key: "require_auth", label: "Require auth", format: enabledLabel },
  {
    key: "accepted_methods",
    label: "Accepted methods",
    format: (v) => (Array.isArray(v) ? v : []).join(", ") || "—",
    equal: arraysEqual,
  },
  {
    key: "required_methods",
    label: "Required methods",
    format: (v) => (Array.isArray(v) ? v : []).join(", ") || "—",
    equal: arraysEqual,
  },
  { key: "leeway_seconds", label: "Leeway", format: (v) => `${v}s` },
  { key: "mtls_ca_pem", label: "mTLS client CA", format: (v) => (v && String(v).trim() ? `${(String(v).match(/BEGIN CERTIFICATE/g) || []).length} certificate(s)` : "none"), equal: (a, b) => String(a || "").trim() === String(b || "").trim() },
  { key: "mtls_cert_pins", label: "mTLS pinned certificates", format: (v) => (Array.isArray(v) ? v.length : 0) + " pinned", equal: arraysEqual },
  { key: "mtls_allowed_subjects", label: "mTLS allowed subjects", format: (v) => (Array.isArray(v) ? v : []).join(", ") || "any", equal: arraysEqual },
];

const MODES = [
  { value: "off", label: "Off", icon: PowerOff, tone: "off" },
  { value: "log_only", label: "Log only", icon: Eye, tone: "log" },
  { value: "enforce", label: "Enforce", icon: ShieldCheck, tone: "enforce" },
];

const IDP_KINDS = [
  { value: "okta",          label: "Okta" },
  { value: "auth0",         label: "Auth0" },
  { value: "azure_ad",      label: "Azure AD" },
  { value: "google",        label: "Google" },
  { value: "oidc_generic",  label: "Generic OIDC" },
];

function emptyIdpConnector() {
  return {
    name: "",
    kind: "okta",
    issuer: "",
    jwks_url: "",
    audience: "",
    alg_allowlist: "RS256, ES256",
  };
}

function idpRowToDraft(row) {
  const algs = row.alg_allowlist || row.algorithms || [];
  const audiences = Array.isArray(row.audience) ? row.audience : (row.audience ? [row.audience] : []);
  return {
    name: row.name || "",
    kind: row.kind || "oidc_generic",
    issuer: row.issuer || "",
    jwks_url: row.jwks_url || "",
    audience: audiences.join(", "),
    alg_allowlist: algs.join(", "),
  };
}

function splitCommaList(value) {
  return (value || "").split(",").map((s) => s.trim()).filter(Boolean);
}

function idpDraftToCreatePayload(draft) {
  return {
    name: draft.name.trim(),
    kind: draft.kind,
    issuer: draft.issuer.trim(),
    jwks_url: draft.jwks_url.trim() || undefined,
    audience: splitCommaList(draft.audience),
    alg_allowlist: splitCommaList(draft.alg_allowlist),
  };
}

function idpDraftToUpdatePayload(draft) {
  return {
    issuer: draft.issuer.trim(),
    jwks_url: draft.jwks_url.trim() || undefined,
    audience: splitCommaList(draft.audience),
    alg_allowlist: splitCommaList(draft.alg_allowlist),
  };
}

const EXEMPT_KINDS = [
  { value: "prefix", label: "Prefix" },
  { value: "exact",  label: "Exact" },
  { value: "regex",  label: "Regex" },
  { value: "glob",   label: "Glob" },
];

const TAB_KEYS = ["policy", "idp", "keys", "hmac", "exempt"];

function policyFieldChanged(k, a, b) {
  if (k === "accepted_methods" || k === "required_methods") {
    const aa = Array.isArray(a) ? a : [];
    const bb = Array.isArray(b) ? b : [];
    if (aa.length !== bb.length) return true;
    const sa = [...aa].sort();
    const sb = [...bb].sort();
    return sa.some((x, i) => x !== sb[i]);
  }
  return a !== b;
}

function toPolicyPayload(draft) {
  if (!draft) return {};
  return POLICY_KEYS.reduce((body, key) => {
    if (draft[key] !== undefined) body[key] = draft[key];
    return body;
  }, {});
}

function buildHeroMetrics(policy, idps, apiKeys, exempts, onAddExempt) {
  const mode = policy?.mode || "off";
  const leeway = policy?.leeway_seconds ?? 60;
  const accepted = Array.isArray(policy?.accepted_methods) && policy.accepted_methods.length
    ? policy.accepted_methods.join(", ") : "—";
  const requireAuth = !!policy?.require_auth;
  const idpCount = idps?.length || 0;
  const keyCount = apiKeys?.filter((k) => !k.revoked_at).length || 0;
  const algSummary = idps?.[0]?.alg_allowlist?.[0] || idps?.[0]?.algorithms?.[0] || "—";

  const modeLabel = mode === "log_only" ? "Log only" : mode === "enforce" ? "Enforce" : "Off";
  const modeTone = mode === "enforce"
    ? { kind: "ok", lbl: "Enforcing" }
    : mode === "log_only"
      ? { kind: "warn", lbl: "Observing" }
      : { kind: "default", lbl: "Off" };

  return [
    {
      key: "mode",
      icon: Shield,
      highlight: true,
      label: "Active mode",
      value: (
        <AsecHeroModeValue label={modeLabel} pillKind={modeTone.kind} pillLabel={modeTone.lbl} />
      ),
      sub: (
        <>
          Require auth <strong style={{ color: requireAuth ? "#0f7a42" : "#A11228" }}>{requireAuth ? "on" : "off"}</strong>
          {" · accepts "}<AsecMono>{accepted}</AsecMono>{` · ${leeway}s leeway`}
        </>
      ),
    },
    {
      key: "requests",
      icon: Activity,
      label: "Requests · 24h",
      value: "—",
      sub: "Not available yet",
    },
    {
      key: "failures",
      icon: ShieldX,
      label: "Auth failures",
      value: <span style={{ color: "#C29021" }}>—</span>,
      sub: "Not available yet",
    },
    {
      key: "credentials",
      icon: Fingerprint,
      label: "Credentials",
      value: <AsecHeroStatValue primary={idpCount} secondary={`IdP · ${keyCount} keys`} />,
      sub: `${algSummary} · ${keyCount === 0 ? "no API keys minted" : `${keyCount} API key${keyCount === 1 ? "" : "s"}`}`,
    },
    {
      key: "exempt",
      icon: DoorOpen,
      label: "Exempt paths",
      value: <AsecHeroStatValue primary={exempts?.length || 0} secondary="routes" />,
      sub: (
        <Typography
          component="span"
          onClick={onAddExempt}
          sx={{ color: colors.primary[300], fontWeight: 500, cursor: "pointer" }}
        >
          add public path →
        </Typography>
      ),
    },
  ];
}

function ChipsInput({ values, suggestions, onChange, placeholder, disabled, uniformHeight = false, sx }) {
  const [draft, setDraft] = React.useState("");
  const addRaw = (s) => {
    const v = (s || "").trim().replace(/,$/, "");
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
  };
  const rest = (suggestions || []).filter((s) => !values.includes(s));
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 0.75, flexWrap: uniformHeight ? "nowrap" : "wrap",
      minHeight: 40,
      ...(uniformHeight ? { height: 40, maxHeight: 40, overflow: "hidden" } : {}),
      p: "6px 10px",
      border: `1px solid ${colors.neutral[300]}`, borderRadius: "8px", backgroundColor: "#fff",
      ...sx,
    }}>
      {values.map((v) => (
        <Box key={v} sx={{
          display: "inline-flex", alignItems: "center", gap: 0.375, height: 26,
          pl: "9px", pr: disabled ? "9px" : "5px", borderRadius: "6px",
          backgroundColor: colors.primary[100],
          color: colors.primary[500],
          border: `1px solid ${colors.primary[200]}`,
          fontSize: 12, fontWeight: 600, lineHeight: "18px",
        }}>
          {v}
          {!disabled && (
            <Box
              onClick={() => onChange(values.filter((x) => x !== v))}
              sx={{
                width: 16, height: 16, borderRadius: "4px", flexShrink: 0,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: colors.primary[400],
                "&:hover": {
                  color: colors.primary[500],
                  backgroundColor: "rgba(99, 113, 247, 0.12)",
                },
              }}
            >
              <LayerdIcon icon={X} size={11} color="inherit" strokeWidth={2} />
            </Box>
          )}
        </Box>
      ))}
      {!disabled && rest.map((s) => (
        <Box
          key={s}
          onClick={() => addRaw(s)}
          sx={{
            height: 26, px: "9px", borderRadius: "6px",
            border: `1px dashed ${colors.neutral[300]}`, color: colors.neutral[600],
            fontSize: 12, lineHeight: "18px", fontWeight: 500,
            display: "inline-flex", alignItems: "center", gap: 0.375, cursor: "pointer",
            backgroundColor: colors.neutral[50],
            transition: "all 120ms",
            "&:hover": {
              borderColor: colors.primary[200],
              color: colors.primary[500],
              backgroundColor: colors.primary[100],
            },
          }}
        >
          <LayerdIcon icon={Plus} size={11} color="inherit" strokeWidth={2} />{s}
        </Box>
      ))}
      <Box
        component="input"
        disabled={disabled}
        value={draft}
        placeholder={values.length === 0 && (!suggestions || suggestions.length === 0) ? placeholder : "add…"}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addRaw(draft);
            setDraft("");
          } else if (e.key === "Backspace" && !draft && values.length > 0) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={() => { addRaw(draft); setDraft(""); }}
        sx={{
          border: 0, outline: 0, flex: 1, minWidth: 80, height: 26,
          fontSize: 14, backgroundColor: "transparent", p: 0, color: colors.neutral[1000],
                  }}
      />
    </Box>
  );
}

function StatusDot({ kind, children }) {
  const palette = kind === "active"
    ? { fg: "#0f7a42", dot: "#0f7a42" }
    : kind === "paused"
      ? { fg: "#7a5b10", dot: "#F2B429" }
      : { fg: colors.neutral[700], dot: colors.neutral[600] };
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 12, fontWeight: 500, color: palette.fg }}>
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: palette.dot }} />
      {children}
    </Stack>
  );
}

function Tag({ kind, children }) {
  const palette = kind === "alg"
    ? { bg: "#F3EEFE", fg: "#6B3FA0", bd: "#E2D6FA" }
    : kind === "scope"
      ? { bg: colors.primary[100], fg: colors.primary[500], bd: colors.primary[200] }
      : { bg: colors.neutral[100], fg: colors.neutral[1000], bd: colors.neutral[200] };
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", height: 22, px: 1,
      borderRadius: "6px", fontSize: 12, fontWeight: 600,
      backgroundColor: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}`,
    }}>{children}</Box>
  );
}

function KindTag({ icon, kind, children }) {
  const isOidc = kind === "oidc";
  const palette = isOidc
    ? { bg: "rgba(99,113,247,0.08)", fg: colors.primary[500], bd: "rgba(99,113,247,0.20)" }
    : { bg: colors.neutral[100], fg: colors.neutral[1000], bd: colors.neutral[200] };
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75,
      fontSize: 12, fontWeight: 600,
      px: "9px", py: "3px", borderRadius: "6px",
      backgroundColor: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}`,
    }}>
      {icon && <LayerdIcon icon={icon} size="xs" color={isOidc ? "primary" : "neutral"} />}{children}
    </Box>
  );
}

function NameCell({ icon, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.125} sx={{ fontWeight: 600, color: colors.neutral[1000] }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: "8px",
        backgroundColor: "rgba(99,113,247,0.10)", color: colors.primary[300],
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <LayerdIcon icon={icon} size="sm" color="primary" />
      </Box>
      {children}
    </Stack>
  );
}

function TableFoot({ children }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      px: 2.25, py: 1.5, borderTop: `1px solid ${colors.neutral[200]}`,
      fontSize: 12, color: colors.neutral[700],
    }}>
      <span>{children}</span>
    </Box>
  );
}

function SearchToolbar({ value, setValue, placeholder }) {
  return (
    <AsecEventsFilterBar
      sx={{ mb: 1.75 }}
      search={value}
      onSearchChange={setValue}
      searchPlaceholder={placeholder}
    />
  );
}

function Stepper({ value, onChange, min = 0, max = 3600, step = 5, disabled }) {
  const btnSx = {
    width: "100%",
    height: "100%",
    minWidth: 0,
    borderRadius: 0,
    color: colors.neutral[700],
    backgroundColor: colors.neutral[50],
    "&:hover": { backgroundColor: colors.neutral[100] },
  };

  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: "40px minmax(0, 1fr) 40px",
      alignItems: "stretch",
      border: `1px solid ${colors.neutral[300]}`,
      borderRadius: "8px",
      overflow: "hidden",
      backgroundColor: "#fff",
      height: 40,
      width: 200,
      flexShrink: 0,
    }}>
      <IconButton
        disabled={disabled || value <= min}
        onClick={() => onChange(Math.max(min, value - step))}
        aria-label="Decrease"
        sx={btnSx}
      >
        <Minus size={16} />
      </IconButton>
      <Box
        component="input"
        type="number"
        disabled={disabled}
        value={value}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          if (Number.isNaN(v)) return;
          onChange(Math.min(max, Math.max(min, v)));
        }}
        sx={{
          width: "100%",
          minWidth: 0,
          border: 0,
          outline: 0,
          textAlign: "center",
          fontSize: 14,
          fontWeight: 600,
          color: colors.neutral[1000],
          fontVariantNumeric: "tabular-nums",
          borderLeft: `1px solid ${colors.neutral[200]}`,
          borderRight: `1px solid ${colors.neutral[200]}`,
          MozAppearance: "textfield",
          "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
        }}
      />
      <IconButton
        disabled={disabled || value >= max}
        onClick={() => onChange(Math.min(max, value + step))}
        aria-label="Increase"
        sx={btnSx}
      >
        <Plus size={16} />
      </IconButton>
    </Box>
  );
}

function FlowStep({ n, iconN, ok, warn, name, desc, last }) {
  const palette = warn
    ? { bg: "#FFF6D0", fg: "#7a5b10" }
    : ok
      ? { bg: "#E8FBEF", fg: "#0f7a42" }
      : { bg: colors.neutral[100], fg: colors.neutral[700] };
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5}
      sx={{ px: 2, py: 1.5, borderBottom: last ? 0 : `1px solid ${colors.neutral[100]}` }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, fontSize: 12, fontWeight: 700,
        backgroundColor: palette.bg, color: palette.fg,
      }}>
        {iconN ? <LayerdIcon icon={Eye} size="xs" /> : n}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.neutral[1000], mb: 0.25 }}>{name}</Typography>
        <Typography sx={{ fontSize: 12, color: colors.neutral[700], lineHeight: "25px" }}>{desc}</Typography>
      </Box>
    </Stack>
  );
}

function Note({ kind, children }) {
  const palette = kind === "warn"
    ? { bg: "#FFFCEF", bd: "#FFE89A", ic: "#C29021" }
    : { bg: "rgba(99,113,247,0.06)", bd: "rgba(99,113,247,0.18)", ic: colors.primary[300] };
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start"
      sx={{
        p: "11px 13px", borderRadius: "8px",
        backgroundColor: palette.bg, border: `1px solid ${palette.bd}`,
        fontSize: 12, color: colors.neutral[700], lineHeight: "25px",
      }}>
      {kind === "warn"
        ? <WarningAmberIcon sx={{ fontSize: 16, color: palette.ic, mt: "1px" }} />
        : <InfoOutlinedIcon sx={{ fontSize: 16, color: palette.ic, mt: "1px" }} />}
      <Box>{children}</Box>
    </Stack>
  );
}

function PolicyTab({ policyDraft, patch, canConfigure, exemptCount }) {
  const accepted = Array.isArray(policyDraft.accepted_methods) ? policyDraft.accepted_methods : [];
  const required = Array.isArray(policyDraft.required_methods) ? policyDraft.required_methods : [];
  const modePillKind = policyDraft.mode === "enforce" ? "ok" : policyDraft.mode === "off" ? "default" : "warn";
  const modePillLabel = policyDraft.mode === "enforce" ? "enforcing" : policyDraft.mode === "off" ? "off" : "log only";

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 2.5, alignItems: "start" }}>
      <AsecSectionCard
        title="Auth policy"
        icon={SlidersHorizontal}
        right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>applies to all sites in this project</Typography>}
        radius="12px"
      >
        <Stack spacing={2.5}>
          <AsecField label="Mode" helper={<>In <strong>Log only</strong>, failures are recorded but requests still reach origin. Switch to <strong>Enforce</strong> to return <AsecMono>401</AsecMono> on a failed check.</>}>
            <AsecModeSegmented
              value={policyDraft.mode || "off"}
              onChange={(v) => patch({ mode: v })}
              disabled={!canConfigure}
              options={MODES}
            />
          </AsecField>

          <AsecField label="Require authentication">
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}
              sx={{ p: "13px 14px", border: `1px solid ${colors.neutral[300]}`, borderRadius: "10px", backgroundColor: "#fff" }}>
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.neutral[1000], mb: 0.25 }}>
                  Reject unauthenticated requests
                </Typography>
                <Typography sx={{ fontSize: 12, color: colors.neutral[700], lineHeight: "25px" }}>
                  When on, any request without a valid credential is denied — even on routes with no specific rule. Exempt paths still bypass.
                </Typography>
              </Box>
              <GreenSwitch
                checked={!!policyDraft.require_auth}
                disabled={!canConfigure}
                onChange={() => patch({ require_auth: !policyDraft.require_auth })}
              />
            </Stack>
          </AsecField>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <AsecField label="Accepted methods" helper="Credential types the gateway will accept on a request.">
              <ChipsInput values={accepted} disabled={!canConfigure}
                suggestions={["jwt", "api_key", "hmac", "mtls"]}
                placeholder="add…"
                onChange={(v) => patch({ accepted_methods: v })} />
            </AsecField>
            <AsecField label="Required methods" optHint="· none = any accepted"
              helper="Force a specific method. Empty means any accepted method passes.">
              <ChipsInput values={required} disabled={!canConfigure}
                suggestions={["hmac", "jwt", "api_key"]}
                placeholder="e.g. hmac for /webhooks"
                onChange={(v) => patch({ required_methods: v })} />
            </AsecField>
          </Box>

          {/* MCP_MTLS_SITE_2026_11_03 */}
          {(accepted.includes("mtls") || required.includes("mtls")) && (
            <AsecField
              label="mTLS client certificates"
              helper={<>The gateway asks for a client certificate on this site's hostnames and accepts only certificates that chain to these CA(s) and are valid for client authentication. Without a CA no certificate is ever accepted. Browsers that have client certificates installed may show a certificate picker on this site.</>}
            >
              <Stack spacing={1.25}>
                <TextField
                  value={policyDraft.mtls_ca_pem || ""}
                  onChange={(e) => patch({ mtls_ca_pem: e.target.value })}
                  disabled={!canConfigure}
                  multiline minRows={5} maxRows={12} size="small"
                  placeholder={"Client CA certificate(s), PEM\n-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----"}
                  error={policyDraft.mode === "enforce" && !String(policyDraft.mtls_ca_pem || "").trim()}
                  sx={{ "& .MuiOutlinedInput-root": { fontFamily: "monospace", fontSize: 12, backgroundColor: "#fff" } }}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button component="label" size="small" variant="outlined" disabled={!canConfigure}>
                    Upload .pem / .crt
                    <input hidden type="file" accept=".pem,.crt,.cer,text/plain"
                      onChange={(e) => { const f = e.target.files && e.target.files[0]; if (!f) return; f.text().then((t) => patch({ mtls_ca_pem: t })); e.target.value = ""; }} />
                  </Button>
                  {(policyDraft.mtls_ca_summary?.certs || []).map((c) => (
                    <Typography key={c.sha256} sx={{ fontSize: 12, color: colors.neutral[700], fontFamily: "monospace" }}>
                      {c.subject} · expires {String(c.not_after).slice(0, 10)}
                    </Typography>
                  ))}
                </Stack>
                {policyDraft.mtls_ca_summary?.error && <Alert severity="error">{policyDraft.mtls_ca_summary.error}</Alert>}
                <TextField
                  label="Pinned certificate SHA-256 fingerprints (optional, one per line)"
                  value={(policyDraft.mtls_cert_pins || []).join("\n")}
                  onChange={(e) => patch({ mtls_cert_pins: e.target.value.split(/[\s,]+/).filter(Boolean) })}
                  disabled={!canConfigure} multiline minRows={2} size="small"
                  sx={{ "& .MuiOutlinedInput-root": { fontFamily: "monospace", fontSize: 12, backgroundColor: "#fff" } }}
                />
                <TextField
                  label="Allowed subjects — CN or SAN (optional, one per line)"
                  value={(policyDraft.mtls_allowed_subjects || []).join("\n")}
                  onChange={(e) => patch({ mtls_allowed_subjects: e.target.value.split("\n").map((x) => x.trimStart()) })}
                  disabled={!canConfigure} multiline minRows={2} size="small"
                  sx={{ "& .MuiOutlinedInput-root": { fontFamily: "monospace", fontSize: 12, backgroundColor: "#fff" } }}
                />
              </Stack>
            </AsecField>
          )}

          <AsecField label="Clock-skew leeway" helper={<>Tolerance applied to <AsecMono>exp</AsecMono> and <AsecMono>nbf</AsecMono> claims to absorb clock drift between the gateway and your IdP.</>}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ maxWidth: 340 }}>
              <Stepper value={policyDraft.leeway_seconds ?? 60} disabled={!canConfigure}
                onChange={(v) => patch({ leeway_seconds: v })} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.neutral[700] }}>
                seconds
              </Typography>
            </Stack>
          </AsecField>
        </Stack>
      </AsecSectionCard>

      <Stack spacing={2.5} sx={{ position: "sticky", top: 80 }}>
        <AsecSectionCard
          title="Evaluation order"
          icon={GitMerge}
          right={<AsecStatusPill kind={modePillKind}>{modePillLabel}</AsecStatusPill>}
          radius="12px"
          noPadding
          bodySx={{ p: 0 }}
        >
          <FlowStep n="1" ok name="Exempt path check"
            desc={<><strong>{exemptCount}</strong> pattern{exemptCount === 1 ? "" : "s"} configured. Public paths skip auth entirely.</>} />
          <FlowStep n="2" ok name={<>Extract credential {accepted.slice(0, 1).map((m) => <AsecMono key={m}>{m}</AsecMono>)}</>}
            desc={<>Pulls Bearer token, <AsecMono>X-API-Key</AsecMono>, or HMAC signature header.</>} />
          <FlowStep n="3" ok name="Verify against IdP"
            desc={<>Signature, issuer, audience, <AsecMono>exp/nbf</AsecMono> ± {policyDraft.leeway_seconds ?? 60}s leeway.</>} />
          <FlowStep iconN="eye" warn name={policyDraft.mode === "enforce" ? "On failure → 401" : "On failure → log"}
            desc={policyDraft.mode === "enforce"
              ? <>Returns <AsecMono>401</AsecMono> immediately. Request never reaches origin.</>
              : <>Recorded as <AsecMono>auth_failed</AsecMono>. Request still reaches origin until you enforce.</>}
            last />
        </AsecSectionCard>

        <AsecSectionCard title="Sample failure" icon={Terminal} radius="12px" noPadding bodySx={{ p: 0 }}>
          <Box component="pre" sx={{
            m: 0, fontSize: 12, lineHeight: "20px",
            backgroundColor: "#0E1116", color: "#E6EBF2", p: "14px 16px",
            whiteSpace: "pre", overflowX: "auto", borderRadius: "0 0 12px 12px",
          }}>
{"{\n  "}<span style={{ color: "#7AA2F7" }}>"event"</span>: <span style={{ color: "#9ECE6A" }}>"auth_failed"</span>,{"\n  "}
<span style={{ color: "#7AA2F7" }}>"reason"</span>: <span style={{ color: "#9ECE6A" }}>"token_expired"</span>,{"\n  "}
<span style={{ color: "#7AA2F7" }}>"method"</span>: <span style={{ color: "#9ECE6A" }}>"jwt"</span>,{"\n  "}
<span style={{ color: "#7AA2F7" }}>"issuer"</span>: <span style={{ color: "#9ECE6A" }}>"auth-test.layerd.dev"</span>,{"\n  "}
<span style={{ color: "#7AA2F7" }}>"exp"</span>: <span style={{ color: "#E0AF68" }}>1780820400</span>,{"\n  "}
<span style={{ color: "#7AA2F7" }}>"leeway_s"</span>: <span style={{ color: "#E0AF68" }}>{policyDraft.leeway_seconds ?? 60}</span>,{"\n  "}
<span style={{ color: "#7AA2F7" }}>"action"</span>: <span style={{ color: "#9ECE6A" }}>"{policyDraft.mode === "enforce" ? "blocked" : "logged"}"</span> <span style={{ color: "#5E6878" }}>{policyDraft.mode === "enforce" ? "// 401 returned" : "// would be 401"}</span>
{"\n}"}
          </Box>
        </AsecSectionCard>
      </Stack>
    </Box>
  );
}

function IdpTab({ idps, onRefresh, onDisable, onDelete, canWrite, canDelete, search, setSearch }) {
  const filtered = idps.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.name || "").toLowerCase().includes(q)
      || (r.issuer || "").toLowerCase().includes(q)
      || (r.kind || "").toLowerCase().includes(q);
  });
  return (
    <>
      <SearchToolbar value={search} setValue={setSearch} placeholder="Search connector or issuer…" />
      <Box sx={asecContentCardSx}>
        <TableContainer component={Paper} sx={{ ...asecTablePaperSx, border: 0, borderRadius: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: "18%" }}>Name</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 140 }}>Kind</TableCell>
                <TableCell sx={asecSettingsTableHeadSx}>Issuer</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 150 }}>Audience</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 140 }}>Algorithms</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 120 }}>Status</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 120 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={asecSettingsTableBodySx}>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 5, textAlign: "center", color: colors.neutral[700] }}>
                    {idps.length === 0
                      ? <>No IdP connectors. Click <strong>Add connector</strong> to register one.</>
                      : "No connectors match this filter."}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => {
                const algs = r.alg_allowlist || r.algorithms || [];
                const audiences = Array.isArray(r.audience) ? r.audience : (r.audience ? [r.audience] : []);
                return (
                  <TableRow key={r.id} hover>
                    <TableCell><NameCell icon={Building2}>{r.name}</NameCell></TableCell>
                    <TableCell><KindTag icon={ShieldCheck} kind="oidc">{r.kind || "oidc_generic"}</KindTag></TableCell>
                    <TableCell><Typography sx={{ fontSize: 12 }}>{r.issuer}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize: 12 }}>{audiences.join(", ") || "—"}</Typography></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.625} flexWrap="wrap" useFlexGap>
                        {algs.length === 0
                          ? <Typography sx={{ color: colors.neutral[600], fontSize: 12 }}>—</Typography>
                          : algs.map((a) => <Tag key={a} kind="alg">{a}</Tag>)}
                      </Stack>
                    </TableCell>
                    <TableCell><StatusDot kind={r.disabled_at ? "paused" : "active"}>{r.disabled_at ? "Paused" : "Active"}</StatusDot></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <Tooltip title="Rotate JWKS">
                          <span>
                            <IconButton size="small" sx={asecIconButtonSx} onClick={() => onRefresh(r)} disabled={!canWrite}>
                              <RefreshIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Pause">
                          <span>
                            <IconButton size="small" sx={asecIconButtonSx} onClick={() => onDisable(r)} disabled={!canWrite}>
                              <PauseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton size="small" sx={{ ...asecIconButtonSx, "&:hover": { backgroundColor: "#FEECEE", color: "#A11228" } }}
                              onClick={() => onDelete(r)} disabled={!canDelete}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableFoot>
          {idps.length} connector{idps.length === 1 ? "" : "s"} · <strong style={{ color: colors.neutral[1000] }}>RS256</strong> default · JWKS auto-refreshed hourly
        </TableFoot>
      </Box>
    </>
  );
}

function KeysTab({ keys, onRevoke, canDelete, search, setSearch }) {
  const filtered = keys.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.prefix || "").toLowerCase().includes(q)
      || (r.description || "").toLowerCase().includes(q);
  });
  return (
    <>
      <SearchToolbar value={search} setValue={setSearch} placeholder="Search prefix or description…" />
      <Box sx={asecContentCardSx}>
        <TableContainer component={Paper} sx={{ ...asecTablePaperSx, border: 0, borderRadius: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 170 }}>Prefix</TableCell>
                <TableCell sx={asecSettingsTableHeadSx}>Description</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: "24%" }}>Scopes</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 150 }}>Created</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 120 }}>Status</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 90 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={asecSettingsTableBodySx}>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 5, textAlign: "center", color: colors.neutral[700] }}>
                    {keys.length === 0
                      ? <>No API keys minted. Click <strong>Mint key</strong> to create one — the full key is shown <strong>once</strong> at creation.</>
                      : "No keys match this filter."}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => {
                const scopes = Array.isArray(r.scopes) ? r.scopes : [];
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}
                        sx={{
                          fontSize: 12,
                          backgroundColor: colors.neutral[100], border: `1px solid ${colors.neutral[200]}`,
                          borderRadius: "7px", px: 1, py: 0.5, display: "inline-flex",
                        }}>
                        <span>{r.prefix || "lyk_…"}</span>
                        <Tooltip title="Copy">
                          <IconButton size="small" sx={{ ...asecIconButtonSx, width: 24, height: 24 }}
                            onClick={() => navigator.clipboard?.writeText(r.prefix || "")}>
                            <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>{r.description || <Typography sx={{ color: colors.neutral[600] }}>—</Typography>}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.625} flexWrap="wrap" useFlexGap>
                        {scopes.length === 0
                          ? <Typography sx={{ color: colors.neutral[600], fontSize: 12 }}>—</Typography>
                          : scopes.map((s) => <Tag key={s} kind="scope">{s}</Tag>)}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: colors.neutral[700], fontSize: 12 }}>
                        {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell><StatusDot kind={r.revoked_at ? "paused" : "active"}>{r.revoked_at ? "Revoked" : "Active"}</StatusDot></TableCell>
                    <TableCell align="right">
                      {!r.revoked_at && (
                        <Tooltip title="Revoke">
                          <span>
                            <IconButton size="small" sx={{ ...asecIconButtonSx, "&:hover": { backgroundColor: "#FEECEE", color: "#A11228" } }}
                              onClick={() => onRevoke(r)} disabled={!canDelete}>
                              <BlockIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableFoot>
          {keys.filter((k) => !k.revoked_at).length} active keys · {keys.filter((k) => k.revoked_at).length} revoked · keys are shown once at creation
        </TableFoot>
      </Box>
    </>
  );
}

function HmacTab({ keys, onRevoke, canDelete, search, setSearch }) {
  const filtered = keys.filter((r) => {
    if (!search) return true;
    return (r.key_id || "").toLowerCase().includes(search.toLowerCase());
  });
  return (
    <>
      <SearchToolbar value={search} setValue={setSearch} placeholder="Search key ID…" />
      <Box sx={asecContentCardSx}>
        <TableContainer component={Paper} sx={{ ...asecTablePaperSx, border: 0, borderRadius: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: "20%" }}>Key ID</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 120 }}>Algorithm</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 160 }}>Canonicalization</TableCell>
                <TableCell sx={asecSettingsTableHeadSx}>Scopes</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 120 }}>Status</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 90 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={asecSettingsTableBodySx}>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 5, textAlign: "center", color: colors.neutral[700] }}>
                    {keys.length === 0
                      ? <>No HMAC signing keys. Click <strong>Mint HMAC</strong> for inbound webhook verification.</>
                      : "No keys match this filter."}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => {
                const scopes = Array.isArray(r.scopes) ? r.scopes : [];
                return (
                  <TableRow key={r.id} hover>
                    <TableCell><NameCell icon={Binary}>{r.key_id}</NameCell></TableCell>
                    <TableCell><Tag kind="alg">{(r.alg || "").replace(/^hmac-/, "").toUpperCase() || "HS256"}</Tag></TableCell>
                    <TableCell><KindTag icon={FileCode}>{r.canonicalization || "custom"}</KindTag></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.625} flexWrap="wrap" useFlexGap>
                        {scopes.length === 0
                          ? <Typography sx={{ color: colors.neutral[600], fontSize: 12 }}>—</Typography>
                          : scopes.map((s) => <Tag key={s} kind="scope">{s}</Tag>)}
                      </Stack>
                    </TableCell>
                    <TableCell><StatusDot kind={r.revoked_at ? "paused" : "active"}>{r.revoked_at ? "Revoked" : "Active"}</StatusDot></TableCell>
                    <TableCell align="right">
                      {!r.revoked_at && (
                        <Tooltip title="Revoke">
                          <span>
                            <IconButton size="small" sx={{ ...asecIconButtonSx, "&:hover": { backgroundColor: "#FEECEE", color: "#A11228" } }}
                              onClick={() => onRevoke(r)} disabled={!canDelete}>
                              <BlockIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableFoot>
          {keys.filter((k) => !k.revoked_at).length} signing key{keys.filter((k) => !k.revoked_at).length === 1 ? "" : "s"} · used to verify inbound webhook signatures
        </TableFoot>
      </Box>
    </>
  );
}

function ExemptTab({ exempts, draft, setDraft, onAdd, onDelete, canWrite, canDelete }) {
  return (
    <Stack spacing={2.5}>
      <AsecSectionCard
        title="Add exempt path"
        icon={DoorOpen}
        right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>requests matching these skip authentication entirely</Typography>}
        radius="12px"
      >
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 200px", md: "1fr 200px minmax(128px, 148px)" },
          gap: 1.5,
          alignItems: "flex-end",
        }}>
          <AsecField label="Pattern" inline>
            <AsecTextField
              compact
              fullWidth
              disabled={!canWrite}
              value={draft.pattern}
              placeholder="/health   ·   /public/*   ·   ^/v1/status$"
              onChange={(e) => setDraft({ ...draft, pattern: e.target.value })}
              sx={{ ...asecFormRowControlSx, "& .MuiInputBase-input": { fontSize: 12 } }}
            />
          </AsecField>
          <AsecField label="Match kind" inline>
            <Select fullWidth size="small" value={draft.kind} disabled={!canWrite}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              sx={{ ...asecSelectSx, ...asecFormRowControlSx }}>
              {EXEMPT_KINDS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </AsecField>
          <AsecField hideLabel inline>
            <PrimaryButton
              fullWidth
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={onAdd}
              disabled={!canWrite || !draft.pattern.trim()}
              sx={asecFormPrimaryButtonSx}
            >
              Add path
            </PrimaryButton>
          </AsecField>
        </Box>
        <Note kind="warn">
          Exempt paths bypass <strong>all</strong> auth checks. Keep patterns tight — <AsecMono>/public/*</AsecMono> is safer than a broad <AsecMono>/v1/*</AsecMono>.
        </Note>
      </AsecSectionCard>

      <AsecSectionCard
        title="Existing exempts"
        icon={List}
        right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>{exempts.length} path{exempts.length === 1 ? "" : "s"}</Typography>}
        radius="12px"
        noPadding
        bodySx={{ p: 0 }}
      >
        <TableContainer component={Paper} sx={{ ...asecTablePaperSx, border: 0, borderRadius: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: "40%" }}>Pattern</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 140 }}>Kind</TableCell>
                <TableCell sx={asecSettingsTableHeadSx}>Created</TableCell>
                <TableCell sx={{ ...asecSettingsTableHeadSx, width: 90 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={asecSettingsTableBodySx}>
              {exempts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 5, textAlign: "center", color: colors.neutral[700] }}>
                    No exempt paths configured.
                  </TableCell>
                </TableRow>
              )}
              {exempts.map((r) => {
                const kindIcon = r.kind === "exact" ? CornerDownRight : TextCursorInput;
                return (
                  <TableRow key={r.id} hover>
                    <TableCell><Typography sx={{ fontSize: 12 }}>{r.pattern}</Typography></TableCell>
                    <TableCell><KindTag icon={kindIcon}>{r.kind}</KindTag></TableCell>
                    <TableCell>
                      <Typography sx={{ color: colors.neutral[700], fontSize: 12 }}>
                        {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                        {r.created_by && <> · by {r.created_by}</>}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove">
                        <span>
                          <IconButton size="small" sx={{ ...asecIconButtonSx, "&:hover": { backgroundColor: "#FEECEE", color: "#A11228" } }}
                            onClick={() => onDelete(r)} disabled={!canDelete}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </AsecSectionCard>
    </Stack>
  );
}

function IdpDrawer({
  open, draft, setDraft, isNew, onClose, onSave, saving, nameError, setNameError,
}) {
  if (!draft) return null;

  const canSave = isNew
    ? Boolean(draft.name.trim() && draft.issuer.trim())
    : Boolean(draft.issuer.trim());

  const saveLabel = saving
    ? (isNew ? "Creating…" : "Saving…")
    : (isNew ? "Create connector" : "Save connector");

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "33%",
          minWidth: 480,
          maxWidth: 560,
          backgroundColor: "#fff",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      <Stack sx={{ height: "100%", backgroundColor: "#fff" }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ px: 4, pt: 4, pb: 3, flexShrink: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "8px",
                backgroundColor: "#EEF0FE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LayerdIcon icon={Building2} size={32} color="primary" />
            </Box>
            <Stack spacing={0}>
              <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                <Typography sx={{ fontWeight: 500, fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}>
                  {isNew ? "Add IdP connector" : "Edit IdP connector"}
                </Typography>
                <FormHelpTooltip tooltip="Connect an external identity provider (Okta, Auth0, Azure AD, Google, or generic OIDC) so Layerd can validate JWTs on inbound API requests." />
              </Stack>
              <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                Validate JWTs issued by an external identity provider.
              </Typography>
            </Stack>
          </Stack>
          <IconButton onClick={onClose} aria-label="Close" sx={{ p: 0, mt: 0.25 }}>
            <CloseIcon sx={{ color: colors.neutral[600] }} />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, pt: 4, pb: 0, pl: 4 }}>
          <ReactPerfectScrollbar style={{ height: "100%" }}>
            <Stack
              justifyContent="space-between"
              spacing={4}
              sx={{ pr: 4, pb: 4, minHeight: "100%" }}
            >
              <Stack spacing={4}>
                <SafeTextField
                  fullWidth
                  label="Name"
                  disabled={!isNew}
                  value={draft.name}
                  placeholder="okta-prod"
                  error={Boolean(nameError)}
                  helperText={nameError || undefined}
                  onChange={(e) => {
                    setDraft({ ...draft, name: e.target.value });
                    if (nameError) setNameError(null);
                  }}
                />

                <GreenSelectForm fullWidth variant="outlined">
                  <InputLabel id="idp-provider-label">Provider</InputLabel>
                  <Select
                    labelId="idp-provider-label"
                    label="Provider"
                    fullWidth
                    value={draft.kind}
                    disabled={!isNew}
                    onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                    IconComponent={ArrowdownIcon}
                  >
                    {IDP_KINDS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </GreenSelectForm>

                <SafeTextField
                  fullWidth
                  label="Issuer URL"
                  sanitize={false}
                  value={draft.issuer}
                  placeholder="https://layerd.okta.com"
                  onChange={(e) => setDraft({ ...draft, issuer: e.target.value })}
                />

                <SafeTextField
                  fullWidth
                  label="JWKS URL (optional)"
                  sanitize={false}
                  value={draft.jwks_url}
                  placeholder="https://layerd.okta.com/.well-known/jwks.json"
                  helperText="Derived from issuer if blank."
                  onChange={(e) => setDraft({ ...draft, jwks_url: e.target.value })}
                />

                <SafeTextField
                  fullWidth
                  label="Audience"
                  sanitize={false}
                  value={draft.audience}
                  placeholder="api://traiceai"
                  helperText="Comma-separated values."
                  onChange={(e) => setDraft({ ...draft, audience: e.target.value })}
                />

                <SafeTextField
                  fullWidth
                  label="Algorithm allowlist"
                  value={draft.alg_allowlist}
                  onChange={(e) => setDraft({ ...draft, alg_allowlist: e.target.value })}
                />

                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    minHeight: 48,
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: colors.primary.alpha,
                    border: `solid 1px ${colors.primary[300]}`,
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 24, color: colors.primary[300], flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                    The gateway fetches and caches the JWKS, auto-refreshing keys hourly. Only listed algorithms
                    are accepted — narrow this to what your IdP actually signs with.
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={3}>
                <SaveButton
                  onClick={onSave}
                  disabled={saving || !canSave}
                  loading={saving}
                  label={saveLabel}
                />
                <CancelButton onClick={onClose} disabled={saving} />
              </Stack>
            </Stack>
          </ReactPerfectScrollbar>
        </Box>
      </Stack>
    </Drawer>
  );
}

function SecretRevealModal({ open, onClose, title, icon, what, prefix, secret }) {
  const [revealed, setRevealed] = React.useState(false);
  React.useEffect(() => { if (!open) setRevealed(false); }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, pr: 1 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.125}>
            <LayerdIcon icon={icon} size="sm" color="primary" />
            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>{title}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, color: colors.neutral[700], mt: 0.375 }}>
            Copy the {what} now — it will not be shown again.
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={asecIconButtonSx}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {prefix && (
            <AsecField label="Prefix">
              <Box sx={{
                p: "10px 12px", borderRadius: "8px",
                border: `1px solid ${colors.neutral[300]}`, backgroundColor: colors.neutral[50],
                fontSize: 12,
              }}>{prefix}</Box>
            </AsecField>
          )}
          <AsecField label={`${what} value`} helper="Treat as sensitive. Never commit to version control.">
            <Stack direction="row" alignItems="center" spacing={1.25}
              sx={{
                p: "12px 14px", borderRadius: "8px",
                border: `1px solid ${colors.neutral[300]}`, backgroundColor: "#0E1116",
                fontSize: 14, color: "#E6EBF2",
                wordBreak: "break-all", lineHeight: "20px",
              }}>
              <Box sx={{ flex: 1 }}>
                {revealed
                  ? (secret || <span style={{ color: "#F2B429" }}>— backend did not return a secret —</span>)
                  : "•".repeat(48)}
              </Box>
              <Stack direction="row" spacing={0.75}>
                <Tooltip title={revealed ? "Hide" : "Reveal"}>
                  <IconButton size="small" sx={asecIconButtonSx} onClick={() => setRevealed(!revealed)}>
                    {revealed ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy">
                  <IconButton size="small" sx={asecIconButtonSx} onClick={() => navigator.clipboard?.writeText(secret || "")}>
                    <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </AsecField>
          <Note kind="warn">There is no recovery — if this {what} is lost, revoke the credential and mint a fresh one.</Note>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ ...asecDialogActionsSx, justifyContent: "space-between" }}>
        <Stack direction="row" alignItems="center" spacing={0.875} sx={{ fontSize: 12, color: colors.neutral[700] }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: "#C29021" }} />Visible only once
        </Stack>
        <Button startIcon={<CheckIcon sx={{ fontSize: 16 }} />} onClick={onClose} sx={asecBtnPrimary}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}

function ApiKeyDrawer({ open, onClose, onSubmit, saving, secretResult }) {
  const [form, setForm] = React.useState({ description: "", scopes: [] });
  const [descError, setDescError] = React.useState(null);
  React.useEffect(() => {
    if (!open) {
      setForm({ description: "", scopes: [] });
      setDescError(null);
    }
  }, [open]);

  if (secretResult) {
    return (
      <SecretRevealModal open={open} onClose={onClose}
        title="API key minted" icon={Key} what="API key"
        prefix={secretResult.prefix} secret={secretResult.secret || secretResult.key || secretResult.token} />
    );
  }

  const handleSubmit = () => {
    if (form.description) {
      const descCheck = validateDescription(form.description);
      if (!descCheck.valid) {
        setDescError(descCheck.message);
        return;
      }
    }
    onSubmit({ description: form.description || undefined, scopes: form.scopes });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "33%",
          minWidth: 480,
          backgroundColor: "#fff",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
          borderTopLeftRadius: "8px",
        },
      }}
    >
      <Stack sx={{ height: "100%", backgroundColor: "#fff" }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ px: 4, pt: 4, pb: 3, flexShrink: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "8px",
                backgroundColor: "#EEF0FE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LayerdIcon icon={Key} size={32} color="primary" />
            </Box>
            <Stack spacing={0}>
              <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                <Typography sx={{ fontWeight: 500, fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}>
                  Mint API key
                </Typography>
                <FormHelpTooltip tooltip="Create a scoped API key for machine-to-machine access. The secret is shown once at creation — copy and store it before closing this drawer." />
              </Stack>
              <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                Generates a scoped key. The secret is shown once — store it safely.
              </Typography>
            </Stack>
          </Stack>
          <IconButton onClick={onClose} aria-label="Close" sx={{ p: 0, mt: 0.25 }}>
            <CloseIcon sx={{ color: colors.neutral[600] }} />
          </IconButton>
        </Stack>

        <Box sx={{ py: 4, pl: 4, flex: 1, minHeight: 0 }}>
          <ReactPerfectScrollbar style={{ height: "100%" }}>
            <Stack
              justifyContent="space-between"
              spacing={4}
              sx={{ pr: 4, minHeight: "100%" }}
            >
              <Stack spacing={4}>
                <SafeTextField
                  fullWidth
                  label="Description"
                  value={form.description}
                  placeholder="CI deploy bot"
                  error={Boolean(descError)}
                  helperText={getCharPolicyFieldText("plainText", descError) || undefined}
                  onChange={(e) => {
                    setForm({ ...form, description: e.target.value });
                    if (descError) setDescError(null);
                  }}
                />
                <AsecField label="Scopes" optHint="· comma"
                  helper="Scopes are matched against per-route requirements. Least privilege wins.">
                  <ChipsInput values={form.scopes}
                    suggestions={["deploy:write", "build:read", "analytics:read", "webhooks:verify"]}
                    placeholder="add scope…"
                    sx={{ minHeight: 48 }}
                    onChange={(v) => setForm({ ...form, scopes: v })} />
                </AsecField>
                <Note kind="warn">The full key is revealed <strong>only once</strong> after creation. There is no way to retrieve it later — revoke and re-mint if lost.</Note>
                <Stack direction="row" alignItems="center" spacing={0.875} sx={{ fontSize: 12, color: colors.neutral[700] }}>
                  <LayerdIcon icon={Lock} size="xs" color="neutral" />Stored as a salted hash
                </Stack>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={3}>
                <SaveButton
                  onClick={handleSubmit}
                  disabled={saving}
                  loading={saving}
                  label={saving ? "Minting…" : "Mint key"}
                />
                <CancelButton onClick={onClose} disabled={saving} />
              </Stack>
            </Stack>
          </ReactPerfectScrollbar>
        </Box>
      </Stack>
    </Drawer>
  );
}

function HmacDrawer({ open, onClose, onSubmit, saving, secretResult }) {
  const [form, setForm] = React.useState({
    key_id: "", canonicalization: "stripe_v1", alg: "hmac-sha256",
    sig_header: "Stripe-Signature", scopes: [],
  });
  React.useEffect(() => {
    if (!open) setForm({ key_id: "", canonicalization: "stripe_v1", alg: "hmac-sha256", sig_header: "Stripe-Signature", scopes: [] });
  }, [open]);

  if (secretResult) {
    return (
      <SecretRevealModal open={open} onClose={onClose}
        title="HMAC key minted" icon={Binary} what="HMAC secret"
        prefix={secretResult.key_id} secret={secretResult.secret || secretResult.key} />
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "33%",
          minWidth: 480,
          backgroundColor: "#fff",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
          borderTopLeftRadius: "8px",
        },
      }}
    >
      <Stack sx={{ height: "100%", backgroundColor: "#fff" }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ px: 4, pt: 4, pb: 3, flexShrink: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "8px",
                backgroundColor: "#EEF0FE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LayerdIcon icon={Binary} size={32} color="primary" />
            </Box>
            <Stack spacing={0}>
              <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                <Typography sx={{ fontWeight: 500, fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}>
                  Mint HMAC key
                </Typography>
                <FormHelpTooltip tooltip="Generate a shared HMAC secret for verifying signed inbound requests such as webhooks. The signing key is shown once at creation." />
              </Stack>
              <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                Shared secret for verifying signed inbound requests (e.g. webhooks).
              </Typography>
            </Stack>
          </Stack>
          <IconButton onClick={onClose} aria-label="Close" sx={{ p: 0, mt: 0.25 }}>
            <CloseIcon sx={{ color: colors.neutral[600] }} />
          </IconButton>
        </Stack>

        <Box sx={{ py: 4, pl: 4, flex: 1, minHeight: 0 }}>
          <ReactPerfectScrollbar style={{ height: "100%" }}>
            <Stack
              justifyContent="space-between"
              spacing={4}
              sx={{ pr: 4, minHeight: "100%" }}
            >
              <Stack spacing={4}>
                <SafeTextField
                  fullWidth
                  label="Key ID"
                  value={form.key_id}
                  placeholder="stripe-webhooks"
                  sanitize={false}
                  onChange={(e) => setForm({ ...form, key_id: e.target.value })}
                />
                <GreenSelectForm fullWidth variant="outlined">
                  <InputLabel id="hmac-canonicalization-label">Canonicalization preset</InputLabel>
                  <Select
                    labelId="hmac-canonicalization-label"
                    label="Canonicalization preset"
                    fullWidth
                    value={form.canonicalization}
                    onChange={(e) => setForm({ ...form, canonicalization: e.target.value })}
                    IconComponent={ArrowdownIcon}
                  >
                    {[
                      { value: "stripe_v1", label: "Stripe v1" },
                      { value: "github", label: "GitHub" },
                      { value: "shopify", label: "Shopify" },
                      { value: "custom", label: "Custom" },
                    ].map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </GreenSelectForm>
                <GreenSelectForm fullWidth variant="outlined">
                  <InputLabel id="hmac-algorithm-label">Algorithm</InputLabel>
                  <Select
                    labelId="hmac-algorithm-label"
                    label="Algorithm"
                    fullWidth
                    value={form.alg}
                    onChange={(e) => setForm({ ...form, alg: e.target.value })}
                    IconComponent={ArrowdownIcon}
                  >
                    {[
                      { value: "hmac-sha256", label: "HS256" },
                      { value: "hmac-sha384", label: "HS384" },
                      { value: "hmac-sha512", label: "HS512" },
                    ].map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </GreenSelectForm>
                <SafeTextField
                  fullWidth
                  label="Signature header"
                  value={form.sig_header}
                  placeholder="Stripe-Signature"
                  sanitize={false}
                  onChange={(e) => setForm({ ...form, sig_header: e.target.value })}
                />
                <AsecField label="Scopes" optHint="· comma">
                  <ChipsInput values={form.scopes}
                    suggestions={["webhooks:verify"]}
                    placeholder="add scope…"
                    sx={{ minHeight: 48 }}
                    onChange={(v) => setForm({ ...form, scopes: v })} />
                </AsecField>
                <Note>The <strong>Stripe v1</strong> preset signs <AsecMono>timestamp + "." + body</AsecMono> and checks the <AsecMono>t=</AsecMono>/<AsecMono>v1=</AsecMono> parts of the signature header.</Note>
                <Stack direction="row" alignItems="center" spacing={0.875} sx={{ fontSize: 12, color: colors.neutral[700] }}>
                  <LayerdIcon icon={Clock} size="xs" color="neutral" />5-min timestamp tolerance
                </Stack>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={3}>
                <SaveButton
                  onClick={() => onSubmit({
                    key_id: form.key_id.trim(),
                    alg: form.alg,
                    canonicalization: form.canonicalization,
                    scopes: form.scopes,
                  })}
                  disabled={saving || !form.key_id.trim()}
                  loading={saving}
                  label={saving ? "Minting…" : "Mint HMAC"}
                />
                <CancelButton onClick={onClose} disabled={saving} />
              </Stack>
            </Stack>
          </ReactPerfectScrollbar>
        </Box>
      </Stack>
    </Drawer>
  );
}

function Body({ siteId, onHeaderState }) {
  const { showSuccess, showError } = useNotification();
  const { canConfigure, canWrite, canDelete } = useAsecPermissions();
  const [tab, setTab] = React.useState(0);

  const [policyBaseline, setPolicyBaseline] = React.useState(null);
  const [policyDraft,    setPolicyDraft]    = React.useState(null);
  const [idps,     setIdps]    = React.useState([]);
  const [apiKeys,  setApiKeys] = React.useState([]);
  const [hmacKeys, setHmacKeys] = React.useState([]);
  const [exempts,  setExempts] = React.useState([]);

  const [savingPolicy, setSavingPolicy] = React.useState(false);
  const [idpDrawer, setIdpDrawer] = React.useState(null);
  const [idpSaving, setIdpSaving] = React.useState(false);
  const [idpNameError, setIdpNameError] = React.useState(null);
  const [keyOpen,  setKeyOpen]  = React.useState(false);
  const [keySaving, setKeySaving] = React.useState(false);
  const [keySecret, setKeySecret] = React.useState(null);
  const [hmacOpen, setHmacOpen] = React.useState(false);
  const [hmacSaving, setHmacSaving] = React.useState(false);
  const [hmacSecret, setHmacSecret] = React.useState(null);
  const [exemptDraft, setExemptDraft] = React.useState({ pattern: "", kind: "prefix" });

  const [idpSearch,  setIdpSearch]  = React.useState("");
  const [keySearch,  setKeySearch]  = React.useState("");
  const [hmacSearch, setHmacSearch] = React.useState("");

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    const [p, i, k, h, e] = await Promise.all([
      asec.authPolicy(siteId).catch(() => ({ data: null })),
      asec.idpList(siteId).catch(() => ({ data: [] })),
      asec.apiKeyList(siteId).catch(() => ({ data: [] })),
      asec.hmacKeyList(siteId).catch(() => ({ data: [] })),
      asec.exemptList(siteId).catch(() => ({ data: [] })),
    ]);
    const policy = p.data || { mode: "off", require_auth: false, accepted_methods: ["jwt"], required_methods: [], leeway_seconds: 60 };
    setPolicyBaseline(policy);
    setPolicyDraft(policy);
    setIdps(Array.isArray(i.data) ? i.data : []);
    setApiKeys(Array.isArray(k.data) ? k.data : []);
    setHmacKeys(Array.isArray(h.data) ? h.data : []);
    setExempts(Array.isArray(e.data) ? e.data : []);
  }, [siteId]);
  React.useEffect(() => { reload(); }, [reload]);

  const modifiedFields = React.useMemo(
    () => collectObjectDiff(policyBaseline, policyDraft, AUTH_POLICY_DIFF_FIELDS),
    [policyBaseline, policyDraft],
  );
  const modifiedCount = modifiedFields.length;

  const patchPolicy = React.useCallback((patch) => {
    if (!canConfigure) return;
    setPolicyDraft((prev) => ({ ...prev, ...patch }));
  }, [canConfigure]);

  const discardPolicy = () => setPolicyDraft(policyBaseline);

  const applyPolicy = async () => {
    if (!siteId || !policyDraft || !canConfigure) return;
    setSavingPolicy(true);
    try {
      const body = toPolicyPayload(policyDraft);
      for (const k of MTLS_KEYS) {
        const a = policyBaseline ? policyBaseline[k] : undefined;
        const b = policyDraft[k];
        if (JSON.stringify(a ?? null) !== JSON.stringify(b ?? null)) body[k] = b ?? null;
      }
      await asec.authPolicySet(siteId, body);
      showSuccess("Policy saved");
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      setSavingPolicy(false);
    }
  };

  const saveIdp = async () => {
    if (!idpDrawer || !canWrite) return;
    const { mode, row, draft } = idpDrawer;
    if (mode === "new") {
      const nameCheck = validateName(draft.name);
      if (!nameCheck.valid) {
        setIdpNameError(nameCheck.message);
        return;
      }
    }
    setIdpSaving(true);
    try {
      if (mode === "new") {
        await asec.idpCreate(siteId, idpDraftToCreatePayload(draft));
        showSuccess("Connector added");
      } else {
        await asec.idpUpdate(siteId, row.id, idpDraftToUpdatePayload(draft));
        showSuccess("Connector updated");
      }
      setIdpDrawer(null);
      setIdpNameError(null);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      setIdpSaving(false);
    }
  };

  const refreshIdp = async (r) => {
    try { await asec.idpRefresh(siteId, r.id); showSuccess("JWKS refreshed"); }
    catch (e) { showError(e?.message || String(e)); }
  };

  const disableIdp = async (r) => {
    try { await asec.idpDisable(siteId, r.id); showSuccess("Paused"); await reload(); }
    catch (e) { showError(e?.message || String(e)); }
  };

  const deleteIdp = async (r) => {
    if (!window.confirm(`Delete connector ${r.name}?`)) return;
    try { await asec.idpDelete(siteId, r.id); showSuccess("Deleted"); await reload(); }
    catch (e) { showError(e?.message || String(e)); }
  };

  const submitKey = async (body) => {
    if (!canWrite) return;
    setKeySaving(true);
    try {
      const r = await asec.apiKeyCreate(siteId, body);
      setKeySecret(r.data || {});
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      setKeySaving(false);
    }
  };

  const revokeKey = async (r) => {
    if (!window.confirm("Revoke key?")) return;
    try { await asec.apiKeyRevoke(siteId, r.id); showSuccess("Revoked"); await reload(); }
    catch (e) { showError(e?.message || String(e)); }
  };

  const submitHmac = async (body) => {
    if (!canWrite) return;
    setHmacSaving(true);
    try {
      const r = await asec.hmacKeyCreate(siteId, body);
      setHmacSecret(r.data || {});
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      setHmacSaving(false);
    }
  };

  const revokeHmac = async (r) => {
    if (!window.confirm("Revoke HMAC?")) return;
    try { await asec.hmacKeyRevoke(siteId, r.id); showSuccess("Revoked"); await reload(); }
    catch (e) { showError(e?.message || String(e)); }
  };

  const addExempt = React.useCallback(async () => {
    if (!canWrite || !exemptDraft.pattern.trim()) return;
    try {
      await asec.exemptCreate(siteId, exemptDraft);
      setExemptDraft({ pattern: "", kind: "prefix" });
      await reload();
      showSuccess("Exempt added");
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    }
  }, [canWrite, exemptDraft, siteId, reload, showSuccess, showError]);

  const deleteExempt = async (r) => {
    if (!window.confirm(`Remove exempt ${r.pattern}?`)) return;
    try { await asec.exemptDelete(siteId, r.id); showSuccess("Removed"); await reload(); }
    catch (e) { showError(e?.message || String(e)); }
  };

  const openNewIdp = React.useCallback(() => {
    if (!canWrite) return;
    setIdpNameError(null);
    setIdpDrawer({ mode: "new", row: null, draft: emptyIdpConnector() });
  }, [canWrite]);

  const openEditIdp = React.useCallback((row) => {
    if (!canWrite) return;
    setIdpNameError(null);
    setIdpDrawer({ mode: "edit", row, draft: idpRowToDraft(row) });
  }, [canWrite]);

  const closeIdpDrawer = React.useCallback(() => {
    setIdpDrawer(null);
    setIdpNameError(null);
  }, []);

  const setIdpDraft = React.useCallback((next) => {
    setIdpDrawer((d) => (d ? { ...d, draft: next } : d));
  }, []);

  const openKey = React.useCallback(() => setKeyOpen(true), []);
  const openHmac = React.useCallback(() => setHmacOpen(true), []);
  const goExempt = React.useCallback(() => setTab(4), []);

  React.useEffect(() => {
    onHeaderState?.({
      tab: TAB_KEYS[tab],
      canWrite,
      openIdp: openNewIdp,
      openKey,
      openHmac,
    });
  }, [tab, canWrite, onHeaderState, openNewIdp, openKey, openHmac]);

  if (!policyDraft) {
    return (
      <Box sx={{ py: 5, textAlign: "center", color: colors.neutral[700] }}>
        <CircularProgress size={24} sx={{ mb: 2 }} />
        <Typography>Loading authentication policy…</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2.5}>
      <AsecMetricStrip metrics={buildHeroMetrics(policyDraft, idps, apiKeys, exempts, goExempt)} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={asecTabsSx}>
        <Tab label="Policy" icon={<LayerdIcon icon={SlidersHorizontal} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`IdP (${idps.length})`} icon={<LayerdIcon icon={Building2} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`API keys (${apiKeys.length})`} icon={<LayerdIcon icon={Key} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`HMAC (${hmacKeys.length})`} icon={<LayerdIcon icon={Binary} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`Exempt paths (${exempts.length})`} icon={<LayerdIcon icon={DoorOpen} size="sm" color="inherit" />} iconPosition="start" />
      </Tabs>

      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mt: -1 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: colors.green[300] }} />
        <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>Live · last sync 4s ago</Typography>
      </Stack>

      {TAB_KEYS[tab] === "policy" && (
        <PolicyTab policyDraft={policyDraft} patch={patchPolicy} canConfigure={canConfigure} exemptCount={exempts.length} />
      )}
      {TAB_KEYS[tab] === "idp" && (
        <IdpTab idps={idps}
          search={idpSearch} setSearch={setIdpSearch}
          onRefresh={refreshIdp} onDisable={disableIdp} onDelete={deleteIdp}
          canWrite={canWrite} canDelete={canDelete} />
      )}
      {TAB_KEYS[tab] === "keys" && (
        <KeysTab keys={apiKeys}
          search={keySearch} setSearch={setKeySearch}
          onRevoke={revokeKey}
          canDelete={canDelete} />
      )}
      {TAB_KEYS[tab] === "hmac" && (
        <HmacTab keys={hmacKeys}
          search={hmacSearch} setSearch={setHmacSearch}
          onRevoke={revokeHmac}
          canDelete={canDelete} />
      )}
      {TAB_KEYS[tab] === "exempt" && (
        <ExemptTab exempts={exempts} draft={exemptDraft} setDraft={setExemptDraft}
          onAdd={addExempt} onDelete={deleteExempt}
          canWrite={canWrite} canDelete={canDelete} />
      )}

      {TAB_KEYS[tab] === "policy" && (
        <StickySaveBar
          open={modifiedCount > 0 && canConfigure}
          modifiedCount={modifiedCount}
          modifiedFields={modifiedFields}
          onDiscard={discardPolicy}
          onApply={applyPolicy}
          isApplying={savingPolicy}
        />
      )}

      <IdpDrawer
        open={Boolean(idpDrawer)}
        draft={idpDrawer?.draft}
        setDraft={setIdpDraft}
        isNew={idpDrawer?.mode === "new"}
        onClose={closeIdpDrawer}
        onSave={saveIdp}
        saving={idpSaving}
        nameError={idpNameError}
        setNameError={setIdpNameError}
      />
      <ApiKeyDrawer open={keyOpen} saving={keySaving}
        secretResult={keySecret}
        onClose={() => { setKeyOpen(false); setKeySecret(null); }}
        onSubmit={submitKey} />
      <HmacDrawer open={hmacOpen} saving={hmacSaving}
        secretResult={hmacSecret}
        onClose={() => { setHmacOpen(false); setHmacSecret(null); }}
        onSubmit={submitHmac} />
    </Stack>
  );
}

function renderPrimaryAction(headerState) {
  if (!headerState) return null;
  const { tab, canWrite, openIdp, openKey, openHmac } = headerState;
  switch (tab) {
    case "idp":
      return (
        <PrimaryButton startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={openIdp} disabled={!canWrite}>
          Add connector
        </PrimaryButton>
      );
    case "keys":
      return (
        <PrimaryButton startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={openKey} disabled={!canWrite}>
          Mint key
        </PrimaryButton>
      );
    case "hmac":
      return (
        <PrimaryButton startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={openHmac} disabled={!canWrite}>
          Mint HMAC
        </PrimaryButton>
      );
    default:
      return null;
  }
}

export default function AsecAuth() {
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [headerState, setHeaderState] = React.useState(null);
  return (
    <AsecPage
      featureKey="api.auth_primitives"
      helpTooltip="Authentication primitives. JWT validation via IdP connectors (Okta/Auth0/Azure/Google/Generic OIDC), API key minting with scopes, HMAC verification for webhooks, exempt path bypasses. Mode log_only records failures without blocking; enforce returns 401."
      title="Authentication"
      helmetTitle="API Security — Authentication"
      pageDescription={(
        <>
          Verify caller identity at the edge — validate JWTs against your IdPs, mint scoped API keys and HMAC signing
          secrets, and exempt public paths. <strong>Log only</strong> records failures without blocking;{" "}
          <strong>Enforce</strong> returns <AsecMono>401</AsecMono>.
        </>
      )}
      onRefresh={() => setRefreshKey((k) => k + 1)}
      headerActions={(
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {renderPrimaryAction(headerState)}
        </Stack>
      )}
    >
      {(siteId) => (
        <Body
          key={refreshKey}
          siteId={siteId}
          onHeaderState={setHeaderState}
        />
      )}
    </AsecPage>
  );
}
